
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { touchActivity } from '../../utils/sessionUtils';
import TenantSelectionModal from '../shared/TenantSelectionModal';
import PasswordInput from '../shared/PasswordInput';

function OwnerLogin() {
  const [email, setEmail] = useState('');
  const [ownerUsername, setOwnerUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginStatus, setLoginStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Multi-tenant selection state
  const [tenantSelection, setTenantSelection] = useState(null); // { tenants, owner }

  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

  /** Stores session data in localStorage and navigates to dashboard */
  const completeLogin = (data) => {
    const { user, sessionId } = data;
    const { tenantid, id: ownerid, username } = user;

    localStorage.setItem('authToken', data.token || '');
    localStorage.setItem('sessionId', sessionId);
    localStorage.setItem('tenantid', tenantid);
    localStorage.setItem('tenantId', String(tenantid));
    localStorage.setItem('ownerid', ownerid);
    localStorage.setItem('ownerId', String(ownerid));
    localStorage.setItem('userId', String(ownerid));
    localStorage.setItem('username', username);
    localStorage.setItem('role', user.role || 'admin');
    localStorage.setItem('ownerData', JSON.stringify(user));

    try {
      localStorage.setItem('me', JSON.stringify({ id: user.id, tenantId: user.tenantid }));
      window.__ME__ = { id: user.id, tenantId: user.tenantid };
    } catch {}

    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }

    if (user.termsAcceptedAt) {
      localStorage.setItem('termsAcceptedAt', user.termsAcceptedAt);
    } else {
      localStorage.removeItem('termsAcceptedAt');
    }

    touchActivity();
    navigate(`/${tenantid}/dashboard`);
  };

  const loginowner = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginStatus('');

    try {
      const response = await axios.post(`${baseUrl}/api/tenants/loginowner`, {
        email,
        username: ownerUsername,
        password,
      });

      if (response.data.success) {
        // Check if multiple tenants require selection
        if (response.data.requiresTenantSelection) {
          setTenantSelection({
            tenants: response.data.tenants,
            owner: response.data.owner
          });
          setIsLoading(false);
          return;
        }

        if (response.data.owner) {
          const owner = response.data.owner;
          if (owner.tenantid) {
            // Owner has a single tenant - complete login via select-tenant to get a session
            await selectTenantAndLogin(owner.tenantid);
          } else {
            // No tenant yet - send to tenant creation
            localStorage.setItem('ownerId', owner.id);
            localStorage.setItem('ownerData', JSON.stringify(owner));
            navigate('/tenantadd', { state: owner });
          }
        } else {
          setLoginStatus(response.data.message || 'Login failed');
        }
      } else {
        setLoginStatus(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 401) {
        setLoginStatus('Invalid credentials');
      } else {
        setLoginStatus('Server error. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /** Calls select-tenant to create a proper session, then navigates to dashboard */
  const selectTenantAndLogin = async (tenantId) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}/api/users/select-tenant`,
        {
          username: ownerUsername,
          password,
          tenantId,
          loginType: 'owner'
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setTenantSelection(null);
        completeLogin(response.data);
      } else {
        toast.error(response.data.message || 'Login failed');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(
          error.response.data?.message || 'Maximum concurrent users reached.',
          { autoClose: 8000 }
        );
      } else {
        console.error('Tenant selection error:', error);
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTenantSelect = (tenantId) => {
    selectTenantAndLogin(tenantId);
  };

  const handleAddNewTenant = () => {
    const owner = tenantSelection.owner;
    localStorage.setItem('ownerId', owner.id);
    localStorage.setItem('ownerData', JSON.stringify(owner));
    setTenantSelection(null);
    navigate('/tenantadd', { state: owner });
  };

  return (
    <div className="owner-add-container">
      <div className="card">
        <h4 className="hdr hdr--primary hdr--lg">Login Here</h4>
        <div className="card-body">
          <form onSubmit={loginowner} className="form">
            <div className="form-group">
              <label htmlFor="email">Email:</label>
              <input
                className="textInput"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your Email"
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="username">Username:</label>
              <input
                className="textInput"
                type="text"
                value={ownerUsername}
                onChange={(e) => setOwnerUsername(e.target.value)}
                placeholder="Enter your Username"
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password:</label>
              <PasswordInput
                className="textInput"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your Password"
                required
                disabled={isLoading}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
            {loginStatus && <p style={{ color: 'red' }}>{loginStatus}</p>}
          </form>
        </div>
      </div>

      {tenantSelection && (
        <TenantSelectionModal
          tenants={tenantSelection.tenants}
          onSelect={handleTenantSelect}
          onCancel={() => setTenantSelection(null)}
          isLoading={isLoading}
          onAddNewTenant={handleAddNewTenant}
        />
      )}
    </div>
  );
}

export default OwnerLogin;
